import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { UiDialog } from '@/components/ui/UiDialog';
import type { PlatformCall } from './common';

/**
 * Apply the platform defaults to existing workspaces an operator picks.
 *
 * The defaults are a starting point: a workspace's own items are never edited
 * or removed. "Only where empty" (the default) gives a module only to the
 * workspaces / entities that have none of it yet; "Add what's missing" also
 * adds the defaults a workspace lacks next to its own. See
 * `app.tenancy.defaults_rollout` for the rules.
 *
 * Workspaces are applied one request each, so a long list shows progress and
 * no single request runs for minutes. Preview runs the same apply and rolls
 * it back.
 */

type Mode = 'ONLY_EMPTY' | 'ADD_MISSING';

interface TargetRow {
  module: string;
  entity: string | null;
  existing: number;
  defaults: number;
  state: 'EMPTY' | 'HAS_OWN' | 'NO_DEFAULTS';
  eligible: boolean;
  added: string[];
  failed: string[];
}

interface WorkspaceRow {
  tenant_key: string;
  name: string;
  kind: 'STANDALONE' | 'UKNOWVA' | null;
  result: 'OK' | 'ERROR' | 'NO_ENTITY' | 'BUSY';
  error: string | null;
  entities: number;
  eligible: boolean;
  targets: TargetRow[];
}

interface ModuleMeta { key: string; label: string; scope: 'WORKSPACE' | 'ENTITY' }

interface Coverage { mode: Mode; modules: ModuleMeta[]; workspaces: WorkspaceRow[] }

export interface ApplyModule { key: string; title: string }

const MODE_TEXT: Record<Mode, { label: string; help: string }> = {
  ONLY_EMPTY: {
    label: 'Only where empty (recommended)',
    help: 'A module is added only where the workspace (or entity) has none of it yet. Anything a workspace has set up is left exactly as it is.',
  },
  ADD_MISSING: {
    label: "Add what's missing",
    help: 'Every picked workspace also gets the defaults it lacks next to its own, matched by code or name. Nothing it has is edited or removed.',
  },
};

const KIND_TEXT: Record<string, string> = { STANDALONE: 'Standalone', UKNOWVA: 'uKnowva' };

const RESULT_TEXT: Record<WorkspaceRow['result'], string> = {
  OK: 'Done', ERROR: 'Failed', NO_ENTITY: 'No entity yet', BUSY: 'Busy',
};

/** One module's state on one workspace, summed over its entities. */
function cellOf(ws: WorkspaceRow, m: ModuleMeta, mode: Mode): { text: string; tip: string; on: boolean } {
  const ts = ws.targets.filter((t) => t.module === m.key);
  if (ws.result !== 'OK' || ts.length === 0) return { text: '—', tip: '', on: false };
  const where = (t: TargetRow) => t.entity ?? 'Workspace';
  const tip = ts.map((t) => `${where(t)}: ${t.state === 'NO_DEFAULTS' ? 'no defaults for this workspace type'
    : t.existing === 0 ? 'empty' : `has ${t.existing} of its own`}`).join(' · ');
  const on = ts.filter((t) => t.eligible);
  if (ts.every((t) => t.state === 'NO_DEFAULTS')) return { text: 'No defaults', tip, on: false };
  if (on.length === 0) return { text: 'Already set up', tip, on: false };
  if (mode === 'ADD_MISSING') {
    return { text: on.every((t) => t.state === 'EMPTY') ? 'Empty' : 'Adds missing', tip, on: true };
  }
  if (m.scope === 'WORKSPACE' || on.length === ts.length) return { text: 'Empty', tip, on: true };
  return { text: `${on.length} of ${ts.length} entities empty`, tip, on: true };
}

export function DefaultsApplyDialog({ call, modules, initial, onClose }: {
  call: PlatformCall;
  modules: ApplyModule[];
  initial: string;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<string[]>([initial]);
  const [mode, setMode] = useState<Mode>('ONLY_EMPTY');
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [stage, setStage] = useState<'pick' | 'confirm' | 'running' | 'done'>('pick');
  const [dryRun, setDryRun] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<WorkspaceRow[]>([]);

  const load = useCallback(async () => {
    if (picked.length === 0) {
      setCoverage(null);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const qs = new URLSearchParams([...picked.map((k) => ['modules', k]), ['mode', mode]]);
      setCoverage((await call(`/defaults-apply/coverage?${qs.toString()}`)) as Coverage);
    } catch (e) {
      setErr((e as Error).message);
      setCoverage(null);
    } finally {
      setLoading(false);
    }
  }, [call, picked, mode]);

  useEffect(() => {
    void load();
  }, [load]);

  // A workspace that no longer qualifies (modules or mode changed) drops out.
  useEffect(() => {
    if (!coverage) return;
    const ok = new Set(coverage.workspaces.filter((w) => w.eligible).map((w) => w.tenant_key));
    setChosen((prev) => new Set([...prev].filter((k) => ok.has(k))));
  }, [coverage]);

  const rows = useMemo(() => coverage?.workspaces ?? [], [coverage]);
  const eligible = useMemo(() => rows.filter((w) => w.eligible), [rows]);
  const visible = showAll ? rows : eligible;
  const hidden = rows.length - eligible.length;
  const titleOf = (key: string) => modules.find((m) => m.key === key)?.title ?? key;

  const toggleModule = (key: string) =>
    setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const toggleRow = (key: string) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  const allChosen = eligible.length > 0 && eligible.every((w) => chosen.has(w.tenant_key));

  const run = async (dry: boolean) => {
    const keys = rows.filter((w) => chosen.has(w.tenant_key)).map((w) => w.tenant_key);
    setDryRun(dry);
    setStage('running');
    setErr(null);
    setResults([]);
    setProgress(0);
    const out: WorkspaceRow[] = [];
    for (const key of keys) {
      try {
        const res = (await call('/defaults-apply', {
          method: 'POST',
          body: JSON.stringify({ modules: picked, tenant_keys: [key], mode, dry_run: dry }),
        })) as Coverage;
        out.push(...res.workspaces);
      } catch (e) {
        const ws = rows.find((w) => w.tenant_key === key)!;
        out.push({ ...ws, result: 'ERROR', error: (e as Error).message, targets: [] });
      }
      setProgress(out.length);
      setResults([...out]);
    }
    setStage('done');
  };

  const back = () => {
    setStage('pick');
    setResults([]);
    if (!dryRun) {
      setChosen(new Set());
      void load();
    }
  };

  const busy = stage === 'running';
  const chosenCount = chosen.size;
  const moduleList = picked.map(titleOf).join(', ');

  return (
    <UiDialog
      open
      onClose={busy ? () => undefined : onClose}
      busy={busy}
      maxWidth="lg"
      title="Apply defaults to workspaces"
      subtitle="A workspace's own items are never edited or removed."
      actions={stage === 'pick' ? (
        <>
          <Button variant="outlined" onClick={onClose}>Cancel</Button>
          <Button variant="outlined" disabled={chosenCount === 0 || loading} onClick={() => void run(true)}>
            Preview
          </Button>
          <Button variant="contained" disabled={chosenCount === 0 || loading} onClick={() => setStage('confirm')}>
            Apply to {chosenCount || ''} workspace{chosenCount === 1 ? '' : 's'}
          </Button>
        </>
      ) : stage === 'confirm' ? (
        <>
          <Button variant="outlined" onClick={() => setStage('pick')}>Back</Button>
          <Button variant="contained" onClick={() => void run(false)}>Confirm</Button>
        </>
      ) : (
        <>
          <Button variant="outlined" disabled={busy} onClick={back}>
            {dryRun ? 'Back' : 'Apply to more'}
          </Button>
          {dryRun && (
            <Button variant="contained" disabled={busy} onClick={() => void run(false)}>
              Apply now
            </Button>
          )}
          {!dryRun && <Button variant="contained" disabled={busy} onClick={onClose}>Close</Button>}
        </>
      )}
    >
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}

      {stage === 'pick' && (
        <>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>Modules</Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
            {modules.map((m) => (
              <Chip key={m.key} label={m.title} clickable
                color={picked.includes(m.key) ? 'primary' : 'default'}
                variant={picked.includes(m.key) ? 'filled' : 'outlined'}
                onClick={() => toggleModule(m.key)} />
            ))}
          </Stack>
          <TextField select size="small" label="How" value={mode} sx={{ minWidth: 300, mb: 0.5 }}
            onChange={(e) => setMode(e.target.value as Mode)}>
            {(Object.keys(MODE_TEXT) as Mode[]).map((m) => (
              <MenuItem key={m} value={m}>{MODE_TEXT[m].label}</MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{MODE_TEXT[mode].help}</Typography>

          {picked.length === 0 ? (
            <Alert severity="info">Pick at least one module.</Alert>
          ) : loading && !coverage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
          ) : coverage && (
            <>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {eligible.length} of {rows.length} workspace{rows.length === 1 ? '' : 's'} can take
                  {' '}{moduleList}
                  {hidden > 0 && !showAll ? ` · ${hidden} hidden (already set up, no entity yet or unreachable)` : ''}
                </Typography>
                {loading && <CircularProgress size={16} />}
                <FormControlLabel
                  control={<Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
                  label={<Typography variant="body2">Show all workspaces</Typography>} />
              </Stack>
              <Paper variant="outlined">
                <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                  <TableContainer><Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={allChosen} disabled={eligible.length === 0}
                            indeterminate={chosenCount > 0 && !allChosen}
                            inputProps={{ 'aria-label': 'Select every workspace that can take them' }}
                            onChange={() => setChosen(allChosen ? new Set()
                              : new Set(eligible.map((w) => w.tenant_key)))} />
                        </TableCell>
                        <TableCell>Workspace</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Entities</TableCell>
                        {coverage.modules.map((m) => <TableCell key={m.key}>{titleOf(m.key)}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visible.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4 + coverage.modules.length} align="center"
                            sx={{ color: 'text.secondary', py: 3 }}>
                            {rows.length === 0 ? 'No active workspaces.'
                              : 'Every workspace already has these set up. Turn on Show all workspaces to see them.'}
                          </TableCell>
                        </TableRow>
                      )}
                      {visible.map((w) => (
                        <TableRow key={w.tenant_key} hover={w.eligible}
                          sx={w.eligible ? undefined : { '& td': { color: 'text.disabled' } }}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" disabled={!w.eligible} checked={chosen.has(w.tenant_key)}
                              inputProps={{ 'aria-label': `Select ${w.name}` }}
                              onChange={() => toggleRow(w.tenant_key)} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="inherit">{w.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{w.tenant_key}</Typography>
                          </TableCell>
                          <TableCell>{w.kind ? KIND_TEXT[w.kind] : '—'}</TableCell>
                          <TableCell align="right">{w.result === 'OK' ? w.entities : '—'}</TableCell>
                          {w.result !== 'OK' ? (
                            <TableCell colSpan={coverage.modules.length}>
                              <Tooltip title={w.error ?? ''}>
                                <span>{w.result === 'NO_ENTITY' ? 'No entity yet' : `Unreachable${w.error ? ': ' + w.error.slice(0, 80) : ''}`}</span>
                              </Tooltip>
                            </TableCell>
                          ) : coverage.modules.map((m) => {
                            const c = cellOf(w, m, coverage.mode);
                            return (
                              <TableCell key={m.key}>
                                <Tooltip title={c.tip}>
                                  <Chip size="small" label={c.text} color={c.on ? 'success' : 'default'}
                                    variant={c.on ? 'filled' : 'outlined'} />
                                </Tooltip>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table></TableContainer>
                </Box>
              </Paper>
            </>
          )}
        </>
      )}

      {stage === 'confirm' && (
        <Alert severity="warning">
          Apply {moduleList} to {chosenCount} workspace{chosenCount === 1 ? '' : 's'} ({MODE_TEXT[mode].label.toLowerCase()})?
          Items are created in each workspace and the change is audited. Nothing a workspace already has is changed.
        </Alert>
      )}

      {(stage === 'running' || stage === 'done') && (
        <ResultsView results={results} total={chosenCount} progress={progress} dryRun={dryRun}
          running={busy} titleOf={titleOf} />
      )}
    </UiDialog>
  );
}

function ResultsView({ results, total, progress, dryRun, running, titleOf }: {
  results: WorkspaceRow[];
  total: number;
  progress: number;
  dryRun: boolean;
  running: boolean;
  titleOf: (key: string) => string;
}) {
  const failed = results.filter((w) => w.result !== 'OK' || w.targets.some((t) => t.failed.length > 0)).length;
  return (
    <Box>
      {running ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {dryRun ? 'Previewing' : 'Applying'}: {progress} of {total} workspace{total === 1 ? '' : 's'}…
          </Typography>
          <LinearProgress variant="determinate" value={total ? (progress / total) * 100 : 0} />
        </Box>
      ) : (
        <Alert severity={failed ? 'warning' : dryRun ? 'info' : 'success'} sx={{ mb: 2 }}>
          {dryRun ? 'Preview only: nothing was saved. ' : ''}
          {results.length - failed} of {results.length} workspace{results.length === 1 ? '' : 's'}
          {dryRun ? ' would apply cleanly' : ' done'}{failed ? `; ${failed} with problems (see below)` : ''}.
        </Alert>
      )}
      <Paper variant="outlined">
        <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
          <TableContainer><Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Workspace</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>{dryRun ? 'Would add' : 'Added'}</TableCell>
                <TableCell>Not added</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((w) => {
                const added = w.targets.filter((t) => t.added.length > 0);
                const failedT = w.targets.filter((t) => t.failed.length > 0);
                const where = (t: TargetRow) => `${titleOf(t.module)}${t.entity ? ` (${t.entity})` : ''}`;
                return (
                  <TableRow key={w.tenant_key}>
                    <TableCell>
                      <Typography variant="body2">{w.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{w.tenant_key}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={RESULT_TEXT[w.result]}
                        color={w.result === 'OK' ? 'success' : w.result === 'ERROR' ? 'error' : 'warning'} />
                    </TableCell>
                    <TableCell>
                      {w.result !== 'OK' ? (w.error ?? '—') : added.length === 0 ? 'Nothing new' : added.map((t) => (
                        <Tooltip key={`${t.module}-${t.entity}`} title={t.added.join(', ')}>
                          <Typography variant="body2">{where(t)}: {t.added.length}</Typography>
                        </Tooltip>
                      ))}
                    </TableCell>
                    <TableCell>
                      {failedT.length === 0 ? '—' : failedT.map((t) => (
                        <Typography key={`${t.module}-${t.entity}`} variant="body2" color="error">
                          {where(t)}: {t.failed.join('; ')}
                        </Typography>
                      ))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table></TableContainer>
        </Box>
      </Paper>
    </Box>
  );
}
